import { useState, useEffect } from 'react';
import { courses, calendar, filters, announcements, whyItems } from './data';
import './App.css';

export default function App() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showScroll, setShowScroll] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const filtered = courses.filter(c => activeFilter === 'all' || c.cat === activeFilter);
  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); };

  return (
    <div className="app">

      {/* ── TOP TICKER ── */}
      <div className="ticker">
        <span>New Session Starting March 2026 &nbsp;—&nbsp; Limited Seats Available &nbsp;|&nbsp; Enroll at <strong>www.corteach.com</strong> &nbsp;|&nbsp; Contact: +92 305 8771119 &nbsp;|&nbsp; support@corteach.com</span>
      </div>

      {/* ── NAVIGATION ── */}
      <nav className="nav">
        <div className="nav-inner">
          <img src="/bmu-logo.png" alt="Baqai Medical University" className="nav-logo-left" />

          <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <li><button onClick={() => scrollTo('programmes')}>Programmes</button></li>
            <li><button onClick={() => scrollTo('calendar')}>Calendar</button></li>
            <li><button onClick={() => scrollTo('about')}>About</button></li>
            <li><button onClick={() => scrollTo('contact')} className="nav-enroll">Enroll Now</button></li>
          </ul>

          <img src="/corteach-logo.png" alt="CorTeach" className="nav-logo-right" />

          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-inner">

          {/* Partnership Strip */}
          <div className="partnership-strip">
            <div className="partner-pill">
              <img src="/bmu-logo.png" alt="Baqai Medical University" className="partner-pill-logo" />
              <div className="partner-pill-text">
                <span className="partner-pill-name">Baqai Medical University</span>
                <span className="partner-pill-role">Academic Institution</span>
              </div>
            </div>
            <div className="partnership-connector">
              <div className="connector-line" />
              <div className="connector-badge">Official Academic Partnership</div>
              <div className="connector-line" />
            </div>
            <div className="partner-pill">
              <img src="/corteach-logo.png" alt="CorTeach" className="partner-pill-logo" />
              <div className="partner-pill-text">
                <span className="partner-pill-name">CorTeach</span>
                <span className="partner-pill-role">Digital Collaborator</span>
              </div>
            </div>
          </div>

          {/* Heading */}
          <div className="hero-text">
            <p className="hero-overline">Baqai Medical University</p>
            <h1 className="hero-title">Online &amp; Blended<br />Learning Programmes</h1>
            <p className="hero-sub">
              Postgraduate certificates, diplomas and awards in medical education, AI in healthcare,
              clinical sciences and research — delivered through CorTeach, Pakistan's foremost
              digital medical education platform.
            </p>
          </div>

          {/* Stats Row */}
          <div className="hero-stats">
            {[['13+','Programmes'],['6','Months Duration'],['100%','Online / Blended'],['2026','New Sessions']].map(([n,l]) => (
              <div key={l} className="hero-stat">
                <strong>{n}</strong><span>{l}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="hero-actions">
            <button className="btn-gold" onClick={() => scrollTo('programmes')}>View All Programmes</button>
            <button className="btn-ghost" onClick={() => scrollTo('calendar')}>Course Calendar</button>
          </div>
        </div>
      </section>

      {/* ── ANNOUNCEMENTS ── */}
      <section className="section section-light" id="about">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Latest Updates</p>
            <h2 className="section-title">Announcements &amp; Notices</h2>
            <div className="section-rule" />
          </div>
          <div className="announcements-grid">
            {announcements.map((a, i) => (
              <div key={i} className="announcement-card">
                <div className="announcement-tag">{a.tag}</div>
                <h4 className="announcement-title">{a.title}</h4>
                <p className="announcement-desc">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROGRAMMES ── */}
      <section className="section section-white" id="programmes">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">BMU × CorTeach Collaboration</p>
            <h2 className="section-title">Postgraduate Programmes</h2>
            <p className="section-desc">All programmes are awarded by Baqai Medical University and delivered digitally through CorTeach.</p>
            <div className="section-rule" />
          </div>

          {/* Filter */}
          <div className="filter-bar">
            {filters.map(f => (
              <button
                key={f.key}
                className={`filter-btn${activeFilter === f.key ? ' active' : ''}`}
                onClick={() => setActiveFilter(f.key)}
              >{f.label}</button>
            ))}
          </div>

          {/* Course Grid */}
          <div className="courses-grid">
            {filtered.map(c => (
              <div key={c.id} className="course-card">
                {c.badge && <span className="course-badge">{c.badge}</span>}
                <div className="course-card-head">
                  <span className="course-abbr">{c.abbr}</span>
                  <span className="course-level">{c.level}</span>
                </div>
                <h3 className="course-title">{c.title}</h3>
                <p className="course-desc">{c.desc}</p>

                <div className="course-meta">
                  {c.meta.map(m => (
                    <div key={m.l} className="meta-cell">
                      <span className="meta-label">{m.l}</span>
                      <span className="meta-value">{m.v}</span>
                    </div>
                  ))}
                </div>

                <div className="course-modules">
                  <p className="modules-label">Programme Modules</p>
                  <ul>
                    {c.modules.map(m => <li key={m}>{m}</li>)}
                  </ul>
                </div>

                <div className="course-footer">
                  <div className="course-fee">
                    <span className="fee-amount">{c.price}</span>
                    <span className="fee-note">{c.priceNote}</span>
                  </div>
                  <a href={c.link} target="_blank" rel="noreferrer" className="enroll-btn">
                    Enrol Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CALENDAR ── */}
      <section className="section calendar-section" id="calendar">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow" style={{color:'#F0D98C'}}>Academic Calendar 2026</p>
            <h2 className="section-title" style={{color:'#fff'}}>Upcoming Session Dates</h2>
            <p className="section-desc" style={{color:'rgba(255,255,255,0.65)'}}>Secure your place before registration closes. All sessions start promptly as advertised.</p>
            <div className="section-rule" style={{background:'linear-gradient(90deg,#C9A84C,#F0D98C)'}} />
          </div>
          <div className="calendar-grid">
            {calendar.map((c, i) => (
              <div key={i} className="calendar-card">
                <div className="cal-month">{c.month}</div>
                <div className="cal-name">{c.name}</div>
                <div className="cal-detail">{c.detail}</div>
                <a href={c.link} target="_blank" rel="noreferrer" className="cal-link">Register Now</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY BMU ── */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Why Choose Us</p>
            <h2 className="section-title">The BMU × CorTeach Advantage</h2>
            <div className="section-rule" />
          </div>
          <div className="why-grid">
            {whyItems.map((w, i) => (
              <div key={i} className="why-card">
                <div className="why-num">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="why-title">{w.title}</h3>
                <p className="why-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT / ENROLL ── */}
      <section className="section cta-section" id="contact">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-text">
              <p className="section-eyebrow" style={{color:'#F0D98C'}}>Ready to Advance Your Career?</p>
              <h2 className="section-title" style={{color:'#fff',marginBottom:16}}>Enrol in a Postgraduate Programme</h2>
              <p style={{color:'rgba(255,255,255,0.7)',fontSize:'1rem',lineHeight:1.7,maxWidth:480}}>
                Join thousands of healthcare and education professionals who have advanced their expertise through Baqai Medical University's online and blended learning programmes.
              </p>
              <div className="cta-actions">
                <a href="https://www.corteach.com/all-courses/" target="_blank" rel="noreferrer" className="btn-gold">Browse All Programmes</a>
                <a href="https://lms.corteach.com" target="_blank" rel="noreferrer" className="btn-ghost">Access LMS</a>
              </div>
            </div>
            <div className="contact-details">
              <div className="contact-block">
                <span className="contact-label">Telephone</span>
                <a href="tel:+923058771119" className="contact-value">+92 305 8771119</a>
              </div>
              <div className="contact-block">
                <span className="contact-label">Email</span>
                <a href="mailto:support@corteach.com" className="contact-value">support@corteach.com</a>
              </div>
              <div className="contact-block">
                <span className="contact-label">Website</span>
                <a href="https://www.corteach.com" target="_blank" rel="noreferrer" className="contact-value">www.corteach.com</a>
              </div>
              <div className="contact-block">
                <span className="contact-label">Social</span>
                <a href="https://www.instagram.com/corteach/" target="_blank" rel="noreferrer" className="contact-value">@corteachofficial</a>
              </div>
              <div className="contact-block">
                <span className="contact-label">Enrolment</span>
                <a href="https://www.corteach.com/calender/chpe/" target="_blank" rel="noreferrer" className="contact-value">www.corteach.com/calender</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-inner">
            <div className="footer-brand">
              <div className="footer-logos">
                <img src="/bmu-logo.png" alt="Baqai Medical University" className="footer-logo-img" />
                <div className="footer-logo-divider" />
                <img src="/corteach-logo.png" alt="CorTeach" className="footer-logo-img" />
              </div>
              <p className="footer-brand-desc">
                A dedicated online learning hub for all postgraduate programmes offered by Baqai Medical University in partnership with CorTeach, Pakistan's foremost digital medical education platform.
              </p>
            </div>

            <div className="footer-col">
              <h4>Programmes</h4>
              <a href="https://www.corteach.com/calender/chpe/" target="_blank" rel="noreferrer">CHPE</a>
              <a href="https://www.corteach.com/calender/code/" target="_blank" rel="noreferrer">CODE</a>
              <a href="https://www.corteach.com/calender/aie/" target="_blank" rel="noreferrer">AIE</a>
              <a href="https://www.corteach.com/calender/cmej/" target="_blank" rel="noreferrer">CMEJ</a>
              <a href="https://www.corteach.com/calender/camh-c/" target="_blank" rel="noreferrer">CAMH-C</a>
              <a href="https://www.corteach.com/all-courses/" target="_blank" rel="noreferrer">View All</a>
            </div>

            <div className="footer-col">
              <h4>Navigation</h4>
              <button onClick={() => scrollTo('programmes')}>All Programmes</button>
              <button onClick={() => scrollTo('calendar')}>Calendar</button>
              <button onClick={() => scrollTo('about')}>Announcements</button>
              <button onClick={() => scrollTo('contact')}>Contact</button>
            </div>

            <div className="footer-col">
              <h4>Contact</h4>
              <a href="tel:+923058771119">+92 305 8771119</a>
              <a href="mailto:support@corteach.com">support@corteach.com</a>
              <a href="https://www.corteach.com" target="_blank" rel="noreferrer">www.corteach.com</a>
              <a href="https://lms.corteach.com" target="_blank" rel="noreferrer">LMS Portal</a>
            </div>
          </div>

          <div className="footer-bottom">
            <span>© 2026 Baqai Medical University. All rights reserved. In partnership with CorTeach.</span>
            <span>Karachi, Pakistan</span>
          </div>
        </div>
      </footer>

      {showScroll && (
        <button className="scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top">
          &#8593;
        </button>
      )}
    </div>
  );
}
